/**
 * URL detection aligned with `BrowseMarkdown` / `Shared/Markdown` `convertLinksToEmbeds`.
 * Used to pick a single embed source from project description text (e.g. browse cards).
 */

// Match web TikTok post URLs; username segment allows any non-slash (not just [\w.]).
const TIKTOK_VIDEO_RE =
  /^https?:\/\/(?:www\.)?tiktok\.com\/@[^/]+\/video\/(\d+)/;

/**
 * @param {string} link
 * @returns {{ embedUrl: string; maxWidth: string; aspectRatio: string }|null}
 */
export function getYoutubeVideoData(link) {
  const youtubeLinkTypes = [
    'youtube.com/watch',
    'youtube.com/shorts',
    'youtu.be',
  ];

  const linkType = youtubeLinkTypes.find((type) => link.includes(type));
  if (!linkType) return null;

  let videoId = '';
  switch (linkType) {
    case 'youtube.com/watch':
      videoId = link.split('v=')[1];
      break;
    case 'youtube.com/shorts':
      videoId = link.split('shorts/')[1];
      break;
    case 'youtu.be':
      videoId = link.split('/').pop();
      break;
    default:
      break;
  }

  if (!videoId) {
    return null;
  }
  videoId = String(videoId).split('&')[0].split('#')[0].split('?')[0];
  if (!videoId) {
    return null;
  }

  const aspectRatio = linkType === 'youtube.com/shorts' ? '0.56/1' : '16/9';
  const maxWidth = linkType === 'youtube.com/shorts' ? '500px' : '100%';

  return {
    embedUrl: `https://www.youtube.com/embed/${videoId}`,
    maxWidth,
    aspectRatio,
  };
}

/**
 * Instagram iframe embed URL (blockquote + embed.js does not work in React).
 *
 * @param {string} url
 * @returns {string|null}
 */
export function getInstagramEmbedSrc(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }
  const m = url.trim().match(/instagram\.com\/(p|reel|tv)\/([^/?#]+)/i);
  if (!m || !m[2]) {
    return null;
  }
  const kind = m[1].toLowerCase();
  const shortcode = m[2];
  return `https://www.instagram.com/${kind}/${shortcode}/embed`;
}

/**
 * True if this href is turned into an iframe/embed component by Portfolio Markdown.
 *
 * @param {string} href
 * @returns {boolean}
 */
export function isEmbeddablePortfolioUrl(href) {
  if (!href || typeof href !== 'string') {
    return false;
  }
  const h = href.trim();
  if (!h.startsWith('http://') && !h.startsWith('https://')) {
    return false;
  }

  if (
    h.includes('youtube.com/watch') ||
    h.includes('youtube.com/shorts') ||
    h.includes('youtu.be')
  ) {
    return !!getYoutubeVideoData(h);
  }

  if (h.includes('vimeo.com')) {
    const match = h.match(/vimeo\.com\/(\d+)/);
    return !!(match && match[1] && match[1].length > 0);
  }

  if (h.includes('soundcloud.com') && !h.includes('on.soundcloud.com')) {
    return true;
  }

  if (h.includes('instagram.com')) {
    return !!getInstagramEmbedSrc(h);
  }

  if (h.includes('tiktok.com')) {
    return TIKTOK_VIDEO_RE.test(h);
  }

  if (h.includes('behance.net/gallery')) {
    const rest = h.split('behance.net/gallery/')[1];
    const postId = rest ? rest.split('/')[0] : '';
    return postId.length > 0;
  }

  return false;
}

/**
 * Collects (index, url) pairs: markdown links first pass, then bare URLs.
 *
 * @param {string} text
 * @returns {Array<{ index: number; url: string }>}
 */
function collectUrlCandidates(text) {
  const out = [];
  if (!text || typeof text !== 'string') {
    return out;
  }

  const mdRe = /\[[^\]]*\]\((https?:\/\/[^)\s]+)\)/g;
  let m;
  while ((m = mdRe.exec(text)) !== null) {
    out.push({ index: m.index, url: m[1] });
  }

  const bareRe = /https?:\/\/[^\s\])"'<>]+/g;
  while ((m = bareRe.exec(text)) !== null) {
    out.push({ index: m.index, url: m[0] });
  }

  out.sort((a, b) => a.index - b.index);
  return out;
}

/**
 * First URL in the description that Markdown would render as a rich embed.
 *
 * @param {string} text
 * @returns {string|null}
 */
export function getFirstEmbeddableUrlFromDescription(text) {
  for (const { url } of collectUrlCandidates(text)) {
    if (isEmbeddablePortfolioUrl(url)) {
      return url;
    }
  }
  return null;
}
